<template>
  <div v-if="customer" class="payment-form-cont">
    <div class="d-flex flex-col justify-content-between mb-3">
      <span class="toggle-label mr-3">Pay With:</span>
      <ButtonToggle
        v-model="paymentType"
        :options="paymentMethods"
        class="full-width toggle"
      />
    </div>
    <div>
      <div v-if="paymentMethods.length && !loadingMethods" class="mt-2">
        <div v-if="paymentType === 'payment_method'" class="mb-3">
          <CustomerPaymentMethods
            :customer="customer"
            :selected-i-d="selectedPaymentMethodID ?? undefined"
            :select-payment-method="handleNewCardToggle"
            @payment-method-removed="handleRemovePaymentMethod"
          />
        </div>
        <div v-if="paymentType === 'gpi_card'">
          <SlotWrapper>
            <template #form>
              <GPICardForm ref="gpiForm" :customer="customer" />
            </template>
          </SlotWrapper>
        </div>
        <div v-if="paymentType === 'stax_card'">
          <SlotWrapper>
            <template #form>
              <StaxCardForm ref="staxCardForm" :customer="customer" />
            </template>
          </SlotWrapper>
        </div>
        <CashForm v-if="paymentType === 'cash'" v-model="receiverId" />
        <CheckForm
          v-if="paymentType === 'check'"
          v-model="receiverId"
          :form="forms.check"
        />
        <ACHForm
          v-if="paymentType === 'gpi_ach'"
          v-model="receiverId"
          :form="forms.gpi_ach"
        />
        <StaxAchForm
          v-if="paymentType === 'stax_ach'"
          ref="staxAchForm"
          :customer="customer"
        />
        <ExternalPaymentForm
          v-if="paymentType === 'external'"
          ref="externalPaymentForm"
          :form="forms.external"
          :financing-enabled="financingEnabled"
          :payment-amount-cents="paymentAmountCents"
          :invoice-id="invoiceId"
          @update:payment-type="handleUpdateExternalPaymentType"
        />
        <TextArea
          v-model="notes"
          label="Notes"
          placeholder="Add notes about this payment"
          class="full-width"
          name="Notes"
          :error-message="firstErrorMessage(v$.notes)"
        ></TextArea>
        <div
          v-if="paymentType === 'external'"
          class="d-flex mt-1 justify-content-end"
        >
          <Checkbox v-model="noNotification" label="Do Not Notify Customer" />
        </div>
      </div>
      <span
        v-if="paymentMethods.length === 0 && !loadingMethods"
        class="no-results"
        >No Payment Methods Available</span
      >
      <span v-if="loadingMethods" class="no-results"
        ><Spinner class="mt-3" message="Loading Payment Methods..."
      /></span>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable simple-import-sort/imports */
import { computed, onMounted, ref, watch } from "vue";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import ButtonToggle from "@/components/ButtonToggle.vue";
import Checkbox from "@/components/Checkbox.vue";
import SlotWrapper from "@/components/SlotWrapper";
import TextArea from "@/components/TextArea.vue";
import useVuelidate from "@vuelidate/core";
import { requiredIf } from "@vuelidate/validators";
import Spinner from "@/components/Spinner.vue";
import ACHForm from "./ACHForm.vue";
import CashForm from "./CashForm.vue";
import CheckForm from "./CheckForm.vue";
import CustomerPaymentMethods from "./CustomerPaymentMethods.vue";
import ExternalPaymentForm from "./ExternalPaymentForm.vue";
import GPICardForm from "./GPICardForm.vue";
import StaxAchForm from "./StaxAchForm.vue";
import StaxCardForm from "./StaxCardForm.vue";
import usePaymentMethods from "../usePaymentMethods";

const props = defineProps({
  invoiceId: {
    type: Number,
    default: null
  },
  financingEnabled: {
    type: Boolean,
    default: false
  },
  paymentAmountCents: {
    type: Number,
    default: 0
  },
  customer: {
    type: Object,
    default: null
  },
  jobId: {
    type: Number,
    default: null
  }
});

const gpiForm = ref(null);
const staxCardForm = ref(null);
const staxAchForm = ref(null);
const externalPaymentForm = ref(null);

const externalPaymentType = ref("");
const paymentType = ref<string | null>(null);
const selectedPaymentMethodID = ref(null);
const addingCard = ref(false);
// Equivalents to window.currentTenant.user_id, needed for Typescript error
const currentUserId = ref(`${(window.currentTenant as any).user_id}_Admin`);
const receiverId = ref<string | null>(null);
const notes = ref("");
const noNotification = ref(false);

const firstName = computed(() => {
  if (!props.customer || !props.customer.full_name) return "";
  const split = props.customer.full_name.split(" ");
  return split.length ? split[0] : "";
});

const lastName = computed(() => {
  if (!props.customer || !props.customer.full_name) return "";
  const split = props.customer.full_name.split(" ");
  return split.length > 1 ? split[1] : "";
});

const forms = ref({
  check: {
    check_dl_number: "",
    check_number: ""
  },
  external: {
    reference_id: "",
    processor_name: "",
    notify_customer: true
  },
  gpi_ach: {
    ach_first_name: firstName.value,
    ach_last_name: lastName.value,
    ach_account_type: "checking",
    ach_account_number: "",
    ach_routing_number: "",
    ach_save_payment_method: false
  }
});
const loadingMethods = ref(true);

const {
  fetchPaymentMethods,
  fetchCustomer: fetchCustomerForPaymentMethods,
  paymentMethodsResponse,
  paymentAmountErrorMessage,
  fetchFinancingLenders,
  receivers
} = usePaymentMethods();

const paymentMethods = computed(() => {
  return paymentMethodsResponse.value.map((method) => {
    if (method.key === "payment_method") {
      return {
        ...method,
        value: method.key,
        disabled: !props.customer.payment_method
      };
    }
    return {
      ...method,
      value: method.key
    };
  });
});

const receiver = computed(() => {
  return receivers.value.find((receiver) => {
    // Finds user based on id and type
    const matchers = receiverId.value?.split("_") ?? [];
    return (
      receiver.id.toString() === matchers[0] &&
      receiver.type.toString() === matchers[1]
    );
  });
});

watch(
  () => props.customer,
  async (newValue, oldValue) => {
    if (newValue && newValue.customer !== oldValue.customer) {
      await refreshData();
      setDefaultPaymentMethod();
    }
  }
);

watch(paymentType, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    clearErrorMessages();
    if (newValue && ["external"].includes(newValue)) {
      setDefaultReceiver();
    }
  }
});

watch(paymentMethods, (newValue, oldValue) => {
  if (newValue === oldValue || !newValue.length || paymentType.value) return;
  // Autoselect first payment method type
  // if method on file then only select if customer has a method on file
  const firstMethod = newValue[0];
  if (firstMethod.key !== "payment_method" || props.customer.payment_method)
    return (paymentType.value = newValue[0].value);
  paymentType.value = newValue[1].value;
});

watch(noNotification, (newValue) => {
  forms.value.external.notify_customer = !newValue;
});

onMounted(() => {
  refreshData();
});

function handleUpdateExternalPaymentType(paymentType) {
  clearErrorMessages();
  externalPaymentType.value = paymentType;
}

function clearErrorMessages() {
  paymentAmountErrorMessage.value = "";
}

async function refreshData() {
  await fetchCustomerForPaymentMethods(props.customer.customer_id);
  await fetchPaymentMethods();
  if (props.financingEnabled) {
    await fetchFinancingLenders();
  }

  loadingMethods.value = false;

  setDefaultReceiver();
  if (props.customer) {
    setDefaultPaymentMethod();
    forms.value.gpi_ach.ach_first_name = firstName.value;
    forms.value.gpi_ach.ach_last_name = lastName.value;
  }
}

async function getFormData(form, params) {
  const cardData = await form.getData();
  return { ...params, ...cardData };
}

async function getData() {
  const validated = await v$.value.$validate();
  if (!validated) return;

  const formData = paymentType.value ? forms.value[paymentType.value] : {};
  const params = {
    transaction_type: getTransactionType(),
    notes: notes.value,
    receiver_id: receiver.value?.id,
    receiver_type: receiver.value?.type,
    ...formData
  };

  if (paymentType.value === "payment_method" && selectedPaymentMethodID.value)
    return { ...params, payment_method_id: selectedPaymentMethodID.value };

  if (paymentType.value === "gpi_card")
    return await getFormData(gpiForm.value, params);

  if (paymentType.value === "stax_ach")
    return await getFormData(staxAchForm.value, params);

  if (paymentType.value === "stax_card")
    return await getFormData(staxCardForm.value, params);

  if (paymentType.value === "external")
    return await getFormData(externalPaymentForm.value, params);

  return params;
}

function getTransactionType() {
  const paymentMethod = paymentMethods.value.find(
    (method) => method.key === paymentType.value
  );
  return paymentMethod ? paymentMethod.transaction_type : paymentType.value;
}

function handleNewCardToggle(isAdding) {
  addingCard.value = isAdding;
  if (addingCard.value) {
    selectedPaymentMethodID.value = null;
  } else {
    setDefaultPaymentMethod();
  }
}

function handleRemovePaymentMethod() {
  selectedPaymentMethodID.value = null;
}

function setDefaultPaymentMethod() {
  if (props.customer && props.customer.payment_method) {
    selectedPaymentMethodID.value = props.customer.payment_method.id;
    addingCard.value = false;
  } else {
    addingCard.value = true;
  }
}

function setDefaultReceiver() {
  receiverId.value = currentUserId.value;
}

const validationRules = {
  notes: {
    requiredIf: requiredIf(() => paymentType.value === "external")
  }
};
const validationState = {
  notes
};
const v$ = useVuelidate(validationRules, validationState, { $autoDirty: true });

defineExpose({
  getData
});
</script>

<style lang="scss" scoped>
.no-results {
  justify-content: center;
  display: flex;
  color: var(--primary-accent);
  text-align: center;
  margin-bottom: 20px;
}

.toggle-label {
  white-space: nowrap;
  font-weight: bold;
}

.toggle {
  flex-wrap: wrap;
}
</style>
